import { useEffect, useState, useRef } from 'react';
import { storageManager } from '../services/storageManager';

/**
 * Hook to show save status in UI
 * Returns: 'idle' | 'pending' | 'saving' | 'saved' | 'error'
 */
export function useSaveStatus() {
  const [status, setStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');

  useEffect(() => {
    const handleStatusChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      setStatus(customEvent.detail);
    };

    window.addEventListener('saveStatusChange', handleStatusChange);

    return () => {
      window.removeEventListener('saveStatusChange', handleStatusChange);
    };
  }, []);

  return status;
}

/**
 * Hook to auto-save warband data when it changes
 * Usage: useAutoSaveWarband(warband)
 */
export function useAutoSaveWarband(warband: any) {
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip first render (avoid saving on initial mount)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    storageManager.saveWarbandChange(warband);
  }, [warband]);
}

/**
 * Hook to auto-save models when they change
 * Usage: useAutoSaveModels(models)
 */
export function useAutoSaveModels(models: any[]) {
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    storageManager.saveModelsChange(models);
  }, [models]);
}

/**
 * Hook to auto-save items when they change
 * Usage: useAutoSaveItems(items)
 */
export function useAutoSaveItems(items: any[]) {
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    storageManager.saveItemsChange(items);
  }, [items]);
}

/**
 * Hook to auto-save locations when they change
 * Usage: useAutoSaveLocations(locations)
 */
export function useAutoSaveLocations(locations: any[]) {
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    storageManager.saveLocationsChange(locations);
  }, [locations]);
}

/**
 * Hook to auto-save templates when they change
 * Usage: useAutoSaveTemplates(modelTemplates, itemTemplates)
 */
export function useAutoSaveTemplates(modelTemplates: any[], itemTemplates: any[]) {
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    storageManager.saveTemplatesChange(modelTemplates, itemTemplates);
  }, [modelTemplates, itemTemplates]);
}

/**
 * Hook to force save before unmounting (use in modals/dialogs)
 * Usage: useForceSaveOnUnmount()
 */
export function useForceSaveOnUnmount() {
  useEffect(() => {
    return () => {
      // Force save when component unmounts
      storageManager.forceSave();
    };
  }, []);
}
